import { all, fork, put, call, cancelled, takeLatest, take, race, takeEvery } from 'redux-saga/effects';

import { executeErrorCode } from '../../auth/actions';
import { studentFinderActionsInterfaces, studentFinderActions, studentFinderActionTypes } from '.';
import { SIGNIN_AS_TEACHER_SUCCESS, LOGOUT_AS_SUCCESS } from '../../authAs/types';

import { getAttributes } from '../../../helpers/urlBuilder';
import StudentFinderService from '../../../services/studentFinder';

const mandatoryColumns = ['semestrAkademicki', 'rokAkademicki', 'studentId', 'studentAlbum', 'studentImie', 'studentNazwisko', 'studentWydzial', 'kierunekNazwa',
  'grupaDziekanska', 'rodzajStudiow', 'studentZdjecie'];

const subjectForStudentListColumns = ['wydzialZlecajacyNazwa', 'wydzialZlecajacyKod', 'przedmiotKod', 'przedmiotNazwa', 'przedmiotId',
  'semestrAkademicki', 'rokAkademicki', 'typPrzydzialu', 'kierownicyKartyLista', 'prowadzacyNazwaLista', 'przedmiotFormaNazwaLista', 'wagiFormyZajec'];

export function* getStudentListDataWatcher(): Generator {
  yield takeLatest(studentFinderActionTypes.GET_STUDENT_LIST, getStudentListData);
}
/**
 * Obsługa zapytania o listę studentów do wyszukiwarki
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudentListData(action: studentFinderActionsInterfaces.GetStudentList): Generator {
  const { search } = action.payload;
  const abortController = new AbortController();
  try {
    const requestAtributes: getAttributes = {
      page: 1,
      columns: mandatoryColumns,
      search,
    };
    const { cancel, response }: any = yield race({
      response: call(StudentFinderService.getStudentList, requestAtributes, abortController.signal),
      cancel: take(studentFinderActionTypes.GET_STUDENT_LIST_CANCEL),
    });
    if(cancel) {
      abortController.abort();
    } else {
      /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
      const payload: GetDataSuccessPayload = {
        ...response.studentPage,
        data: response.studentPage.data.map((obj: any, index: number) => ({
          ...obj,
          key: obj.studentId,
          lp: index + 1,
        }
        )),
      };
      yield put(studentFinderActions.getStudentListSuccess(payload));
    }

  } catch (error) {
    if (error && error.errorCode) {
      yield put(studentFinderActions.getStudentListFail());
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* getSubjectForStudentListWatcher(): Generator {
  yield takeLatest(studentFinderActionTypes.GET_SUBJECT_FOR_STUDENT_LIST, getSubjectForStudentListData);
}
/**
 * Obsługa zapytania o listę przedmiotów dla danego studenta
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectForStudentListData(action: studentFinderActionsInterfaces.GetSubjectForStudentList): Generator {
  const { studentId, semestrAkademicki, rokAkademicki } = action.payload;
  const rokSemestrAkademicki = `${rokAkademicki}-${semestrAkademicki}`;
  const abortController = new AbortController();
  try {
    const requestAtributes: getAttributes = {
      page: 1,
      columns: subjectForStudentListColumns,
    };
    const response: any = yield call(StudentFinderService.getSubjectForStudentList, requestAtributes, studentId, rokSemestrAkademicki, abortController.signal);

    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.przedmiotPage,
      data: response.przedmiotPage.data.map((obj: any) => ({
        ...obj,
        key: `${obj.przedmiotId}_${obj.wydzialZlecajacyKod}`,
      }
      )),
    };
    yield put(studentFinderActions.getSubjectForStudentListSuccess(payload, studentId));

  } catch (error) {
    if (error && error.errorCode) {
      yield put(studentFinderActions.getSubjectForStudentListFail());
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* resetReducerWatcher(): Generator {
  yield takeEvery([SIGNIN_AS_TEACHER_SUCCESS, LOGOUT_AS_SUCCESS], resetReducer);
}
/**
 * Reset reducer do init state w momencie zalogowania jako ktoś inny oraz wylogowania z tego konta
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* resetReducer(): Generator {
  yield put(studentFinderActions.resetReducer());
}

export default function* rootSaga(): Generator {
  yield all([
    fork(getStudentListDataWatcher),
    fork(getSubjectForStudentListWatcher),
    fork(resetReducerWatcher),
  ]);
}